import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Building2, Map, Wheat, Boxes, Warehouse, CircleDollarSign, ArrowRightLeft, Banknote, Beef, Wrench, Milk, FileBarChart2, CalendarCheck, BrainCircuit } from 'lucide-react';
import { Footer } from './LandingPage'; // Reutilizando o footer

const PageHeader = ({ title, subtitle }) => (
  <div className="relative bg-green-900/20 py-20 sm:py-32">
    <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
    <div className="container mx-auto px-4 text-center relative z-10">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="text-4xl md:text-6xl font-bold tracking-tight text-white"
      >
        {title}
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
        className="mt-4 max-w-3xl mx-auto text-lg md:text-xl text-green-100/80"
      >
        {subtitle}
      </motion.p>
    </div>
  </div>
);

const ModulesListSection = () => {
  const modules = [
    { icon: <Building2 className="w-8 h-8 text-amber-400" />, title: "Propriedades", description: "Gerencie múltiplas fazendas com facilidade, consolidando informações em um só lugar." },
    { icon: <Map className="w-8 h-8 text-amber-400" />, title: "Talhões", description: "Controle áreas, culturas, atividades e a produtividade de cada pedaço de sua terra." },
    { icon: <Wheat className="w-8 h-8 text-amber-400" />, title: "Safras", description: "Acompanhe o resultado real, custo por hectare e a margem de lucro de cada ciclo produtivo." },
    { icon: <Boxes className="w-8 h-8 text-amber-400" />, title: "Estoque de Insumos", description: "Controle entradas, saídas, notas fiscais e o custo real de cada aplicação e operação." },
    { icon: <Warehouse className="w-8 h-8 text-amber-400" />, title: "Armazéns de Grãos", description: "Monitore qualidade, umidade, impurezas, contratos de venda e toda a logística." },
    { icon: <CircleDollarSign className="w-8 h-8 text-amber-400" />, title: "Financeiro Completo", description: "Extratos, conciliações, fluxos de caixa, lançamentos e dashboards 100% integrados." },
    { icon: <ArrowRightLeft className="w-8 h-8 text-amber-400" />, title: "Contas a Pagar/Receber", description: "Organização total com baixas automáticas, lembretes e trilha de auditoria completa." },
    { icon: <Banknote className="w-8 h-8 text-amber-400" />, title: "Financiamentos", description: "Gestão de contratos, parcelas automáticas, pagamentos e renegociações com total rastreabilidade." },
    { icon: <Beef className="w-8 h-8 text-amber-400" />, title: "Animais/Rebanho", description: "Controle de lotes, compra, venda, óbito, pesagens e integrações financeiras automáticas." },
    { icon: <Wrench className="w-8 h-8 text-amber-400" />, title: "Ativos e Manutenção", description: "Gerencie máquinas, equipamentos, manutenções preventivas, custos e desempenho." },
    { icon: <Milk className="w-8 h-8 text-amber-400" />, title: "Produção de Leite", description: "Controle de turnos, indicadores de qualidade, volume e a receita gerada por litro." },
    { icon: <FileBarChart2 className="w-8 h-8 text-amber-400" />, title: "Relatórios Gerenciais", description: "KPIs completos para tomada de decisão, auditorias e apresentação para bancos e investidores." },
    { icon: <CalendarCheck className="w-8 h-8 text-amber-400" />, title: "Orçamento Anual", description: "Planeje seu ano agrícola e compare os resultados orçados versus os realizados em tempo real." },
    { icon: <BrainCircuit className="w-8 h-8 text-amber-400" />, title: "Simulador Inteligente", description: "Projete lucros, custos e produtividade com base em cenários de mercado e variáveis climáticas." },
  ];

  return (
    <div className="py-20 sm:py-24 bg-gray-950">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {modules.map((module, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
              className="bg-green-900/20 border border-green-800/30 rounded-lg p-6 flex gap-6 items-start hover:bg-green-900/40 hover:border-green-700 transition-all duration-300"
            >
              <div className="flex-shrink-0 mt-1">
                {module.icon}
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">{module.title}</h3>
                <p className="text-green-100/80">{module.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CtaModulos = () => (
    <section className="py-20 sm:py-24 bg-green-900/10">
        <div className="container mx-auto px-4 text-center">
            <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
            >
                <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">Todos os recursos que sua fazenda precisa, em um só lugar.</h2>
                <p className="mt-4 text-lg text-green-200/70 max-w-xl mx-auto">
                    Simplifique sua gestão e foque no que realmente importa: produzir mais e melhor.
                </p>
                <div className="mt-8">
                    <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-green-950 font-bold text-base w-full sm:w-auto group">
                        <Link to="/">
                            Iniciar meu teste gratuito
                            <ArrowRight className="w-5 h-5 ml-2 transform transition-transform group-hover:translate-x-1" />
                        </Link>
                    </Button>
                </div>
            </motion.div>
        </div>
    </section>
);

export default function Modulos() {
  return (
    <div className="bg-gray-950">
      <PageHeader 
        title="Uma Plataforma, Todas as Soluções"
        subtitle="Conheça em detalhes os módulos que fazem do AgroGestão a ferramenta definitiva para a gestão da sua fazenda."
      />
      <ModulesListSection />
      <CtaModulos />
      <Footer />
    </div>
  );
}
